import React, { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Chip, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const CostIntelligenceTab = ({ costIntelligence }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  if (!costIntelligence || !costIntelligence.summary) {
    return (
      <div className="alert alert-warning">
        <strong>Cost Intelligence Unavailable</strong><br/>
        Cost Intelligence requires AWS Cost and Usage Reports (CUR) to be enabled and configured.<br/>
        Please contact support to set up CUR for detailed cost optimization recommendations.
      </div>
    );
  }

  const { summary, compute, storage, network, containers } = costIntelligence;

  const categories = [
    { key: 'all', label: 'All', count: summary.totalOpportunities },
    { key: 'compute', label: 'Compute', count: summary.categories.compute },
    { key: 'storage', label: 'Storage', count: summary.categories.storage },
    { key: 'network', label: 'Network', count: summary.categories.network },
    { key: 'containers', label: 'Containers', count: summary.categories.containers }
  ];

  const shouldShow = (category) => selectedCategory === 'all' || selectedCategory === category;

  return (
    <div>
      {/* Summary Header */}
      <div className="card shadow-sm border-0 mb-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="card-body text-white">
          <h5 className="mb-2">Cost Intelligence</h5>
          <h4 className="mb-0">{summary.totalOpportunities} Opportunities • ${summary.estimatedMonthlySavings.toFixed(2)}/month savings</h4>
        </div>
      </div>

      {/* Category Pills */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {categories.map(cat => (
          <Chip
            key={cat.key}
            label={`${cat.label} (${cat.count})`}
            onClick={() => setSelectedCategory(cat.key)}
            color={selectedCategory === cat.key ? 'primary' : 'default'}
            variant={selectedCategory === cat.key ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {/* Compute Optimization */}
      {shouldShow('compute') && summary.categories.compute > 0 && (
        <Accordion defaultExpanded={selectedCategory === 'compute'}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="d-flex justify-content-between w-100 align-items-center" style={{ marginRight: '16px' }}>
              <span className="flex-grow-1"><strong>Compute Optimization</strong> ({summary.categories.compute} opportunities)</span>
              <span className="badge bg-success flex-shrink-0 ms-2 text-nowrap">${((compute.ec2?.estimatedSavings || 0) + (compute.rds?.estimatedSavings || 0) + (compute.lambda?.estimatedSavings || 0)).toFixed(2)}/mo</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            {compute.enrollmentRequired ? (
              <div className="alert alert-warning">
                <strong>AWS Compute Optimizer Not Enabled</strong><br/>
                Enable Compute Optimizer in AWS Console to get EC2/RDS/Lambda rightsizing recommendations.
              </div>
            ) : (
              <>
                {compute.ec2?.count > 0 && (
                  <div className="mb-3">
                    <h6>EC2 Rightsizing ({compute.ec2.count})</h6>
                    <small className="text-muted">Estimated savings: ${compute.ec2.estimatedSavings.toFixed(2)}/month</small>
                  </div>
                )}
                {compute.rds?.count > 0 && (
                  <div className="mb-3">
                    <h6>RDS Rightsizing ({compute.rds.count})</h6>
                    <small className="text-muted">Estimated savings: ${compute.rds.estimatedSavings.toFixed(2)}/month</small>
                  </div>
                )}
                {compute.lambda?.count > 0 && (
                  <div>
                    <h6>Lambda Optimization ({compute.lambda.count})</h6>
                    <small className="text-muted">Estimated savings: ${compute.lambda.estimatedSavings.toFixed(2)}/month</small>
                  </div>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Storage Optimization */}
      {shouldShow('storage') && summary.categories.storage > 0 && (
        <Accordion defaultExpanded={selectedCategory === 'storage'}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="d-flex justify-content-between w-100 align-items-center" style={{ marginRight: '16px' }}>
              <span className="flex-grow-1"><strong>Storage Optimization</strong> ({summary.categories.storage} opportunities)</span>
              <span className="badge bg-success flex-shrink-0 ms-2 text-nowrap">${((storage.s3Lifecycle?.estimatedSavings || 0) + (storage.ebsOptimization?.estimatedSavings || 0) + (storage.cloudwatchLogs?.estimatedSavings || 0)).toFixed(2)}/mo</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            {storage.s3Lifecycle?.count > 0 && (
              <div className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">S3 Lifecycle Policies ({storage.s3Lifecycle.count})</h6>
                  <span className="badge bg-info">${storage.s3Lifecycle.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small mb-2">Add lifecycle policies to transition objects to cheaper storage tiers</p>
                {storage.s3Lifecycle.items?.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="text-muted small">• {item.bucketName}</div>
                ))}
              </div>
            )}
            
            {storage.ebsOptimization?.count > 0 && (
              <div className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">EBS gp2 → gp3 Migration ({storage.ebsOptimization.count})</h6>
                  <span className="badge bg-info">${storage.ebsOptimization.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small mb-2">Migrate gp2 volumes to gp3 for 20% cost savings</p>
                {storage.ebsOptimization.items?.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="text-muted small">• {item.volumeId} ({item.size} GB, {item.region})</div>
                ))}
              </div>
            )}

            {storage.cloudwatchLogs?.count > 0 && (
              <div className="p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">CloudWatch Logs Retention ({storage.cloudwatchLogs.count})</h6>
                  <span className="badge bg-info">${storage.cloudwatchLogs.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small mb-2">Set retention policies on log groups (currently: Never Expire)</p>
                {storage.cloudwatchLogs.items?.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="text-muted small">• {item.logGroupName} ({item.region})</div>
                ))}
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Network Optimization */}
      {shouldShow('network') && summary.categories.network > 0 && (
        <Accordion defaultExpanded={selectedCategory === 'network'}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="d-flex justify-content-between w-100 align-items-center" style={{ marginRight: '16px' }}>
              <span className="flex-grow-1"><strong>Network Optimization</strong> ({summary.categories.network} opportunities)</span>
              <span className="badge bg-success flex-shrink-0 ms-2 text-nowrap">${((network.interAZ?.estimatedSavings || 0) + (network.internetOut?.estimatedSavings || 0) + (network.interRegion?.estimatedSavings || 0) + (network.natGateway?.estimatedSavings || 0)).toFixed(2)}/mo</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            {network.interAZ?.count > 0 && (
              <div className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Inter-AZ Data Transfer</h6>
                  <span className="badge bg-info">${network.interAZ.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small mb-1">High cross-AZ traffic: {network.interAZ.usage_gb.toFixed(2)} GB/month</p>
                <p className="text-muted small mb-0">Use single-AZ for non-critical workloads or optimize cross-AZ calls</p>
              </div>
            )}

            {network.internetOut?.count > 0 && (
              <div className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Internet Egress</h6>
                  <span className="badge bg-info">${network.internetOut.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small mb-1">High internet out: {network.internetOut.usage_gb.toFixed(2)} GB/month</p>
                <p className="text-muted small mb-0">Use CloudFront CDN, enable compression, cache static assets</p>
              </div>
            )}

            {network.interRegion?.count > 0 && (
              <div className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Inter-Region Transfer</h6>
                  <span className="badge bg-info">${network.interRegion.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small mb-1">Cross-region traffic: {network.interRegion.usage_gb.toFixed(2)} GB/month</p>
                <p className="text-muted small mb-0">Consolidate regions or use S3 Transfer Acceleration</p>
              </div>
            )}

            {network.natGateway?.count > 0 && (
              <div className="p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">NAT Gateway Optimization</h6>
                  <span className="badge bg-info">${network.natGateway.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small mb-1">NAT Gateway data: {network.natGateway.usage_gb.toFixed(2)} GB/month</p>
                <p className="text-muted small mb-0">Use VPC endpoints for AWS services to bypass NAT Gateway</p>
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Containers Optimization */}
      {shouldShow('containers') && summary.categories.containers > 0 && (
        <Accordion defaultExpanded={selectedCategory === 'containers'}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="d-flex justify-content-between w-100 align-items-center" style={{ marginRight: '16px' }}>
              <span className="flex-grow-1"><strong>Container Optimization</strong> ({summary.categories.containers} opportunities)</span>
              <span className="badge bg-success flex-shrink-0 ms-2 text-nowrap">${(containers.ecrImages?.estimatedSavings || 0).toFixed(2)}/mo</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            {containers.ecrImages?.count > 0 && (
              <div className="p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">ECR Image Cleanup ({containers.ecrImages.count})</h6>
                  <span className="badge bg-info">${containers.ecrImages.estimatedSavings.toFixed(2)}/mo</span>
                </div>
                <p className="text-muted small">Remove images older than 90 days to reduce storage costs</p>
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {summary.totalOpportunities === 0 && (
        <div className="alert alert-info">
          <strong>No Optimization Opportunities Found</strong><br/>
          Based on current CUR data analysis, no immediate cost optimization opportunities were identified.
        </div>
      )}
    </div>
  );
};

export default CostIntelligenceTab;
